import { Link } from "react-router-dom";
import { useLang } from "@/i18n/LanguageContext";
import { PageHero } from "@/components/PageHero";
import { Reveal, SectionHeading } from "@/components/motion";
import { CtaBand } from "@/components/CtaBand";
import { LeafMark } from "@/components/LeafMark";
import { usePageMeta } from "@/lib/seo";
import { IMG } from "@/lib/site";

export const About = () => {
    const { t, tHtml } = useLang();
    usePageMeta(
        t("meta.about.title", "About UniversHeal"),
        t("meta.about.desc", "UniversHeal is a healthcare coordination company for international patients, launching first in Kerala, India. People. Care. Beyond Borders.")
    );
    return (
        <>
            <PageHero
                eyebrow={t("about.eyebrow", "About UniversHeal")}
                title={
                    <>
                        People. Care.{" "}
                        <span className="italic text-pine">Beyond Borders.</span>
                    </>
                }
                intro={t("about.intro", "UniversHeal was created around a simple belief: that world-class healthcare should feel close and human — no matter where in the world you were born.")}
            />

            <section className="section-pad pt-8">
                <div className="container-site">
                    <div className="grid lg:grid-cols-2 gap-10 lg:gap-20">
                        <Reveal>
                            <div className="space-y-6 text-base sm:text-lg leading-relaxed text-charcoal/85">
                                <p>
                                    {t("about.text1", "We are a healthcare coordination company. When you or someone you love needs medical care in India, we walk beside you — helping you understand your options, connecting you with appropriate specialists, and coordinating the practical journey: consultations, treatment planning, travel, recovery and follow-up.")}
                                </p>
                                <p>
                                    {t("about.text2", "Our first market is Kerala, on India's south-western coast — a state with established healthcare infrastructure, deeply experienced medical professionals and a culture of hospitality that makes it a natural home for international patients. Our ambition is to carry this model of personal, accountable coordination across India.")}
                                </p>
                                <p className="text-moss">
                                    {t("about.text3", "We do not provide medical treatment. We do something quieter but just as important: we make sure you never have to navigate it alone.")}
                                </p>
                            </div>
                        </Reveal>
                        <Reveal delay={0.1}>
                            <div className="bg-forest text-ivory rounded-2xl p-8 sm:p-12 h-full flex flex-col justify-between">
                                <LeafMark className="w-10 h-10 text-natural" />
                                <blockquote className="mt-10 font-serif italic text-3xl sm:text-4xl leading-snug">
                                    {t('about.quote', '"The best healthcare journey is the one where someone is always in your corner."')}
                                </blockquote>
                                <p className="mt-8 text-sm text-ivory/60">
                                    {t("about.text4", "The UniversHeal team")}
                                </p>
                            </div>
                        </Reveal>
                    </div>
                </div>
            </section>

            <section className="section-pad pt-0">
                <div className="container-site">
                    <Reveal>
                        <div className="rounded-2xl overflow-hidden h-64 sm:h-[420px] shadow-[0_30px_70px_rgba(6,60,53,0.14)]">
                            <img
                                src={IMG.aboutCare}
                                alt="An Indian healthcare team warmly welcoming an international family into a bright modern hospital lobby"
                                data-testid="about-image"
                                className="w-full h-full object-cover"
                                loading="lazy"
                            />
                        </div>
                    </Reveal>
                </div>
            </section>

            <section className="section-pad bg-white">
                <div className="container-site">
                    <SectionHeading
                        index="—"
                        eyebrow={t("about.eyebrow", "Our Principles")}
                        title={t("about.title1", "How we work.")}
                    />
                    <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-4 gap-8">
                        {[
                            {
                                title: "One contact, always",
                                desc: "A dedicated coordinator who knows your case — never a call centre.",
                            },
                            {
                                title: "Honest information",
                                desc: "Clear facts and clear options. No inflated promises, ever.",
                            },
                            {
                                title: "Verified only",
                                desc: "We coordinate only providers we have reviewed and trust.",
                            },
                            {
                                title: "With you throughout",
                                desc: "From first enquiry to after you return home — support does not expire.",
                            },
                        ].map((p, i) => (
                            <Reveal key={p.title} delay={i * 0.06}>
                                <div data-testid={`principle-${i + 1}`}>
                                    <LeafMark className="w-5 h-5 text-natural" />
                                    <h3 className="mt-4 font-serif font-medium text-2xl text-forest">
                                        {p.title}
                                    </h3>
                                    <p className="mt-2 text-sm leading-relaxed text-moss">
                                        {p.desc}
                                    </p>
                                </div>
                            </Reveal>
                        ))}
                    </div>
                    <Reveal delay={0.1}>
                        <p className="mt-12 text-xs leading-relaxed text-moss max-w-2xl">
                            [Placeholder — team profiles, leadership details and
                            {t("about.text5", "company registration information will be published")}
                            here before launch.]
                        </p>
                    </Reveal>
                    <Reveal delay={0.15}>
                        <Link
                            to="/get-a-medical-opinion"
                            data-testid="about-cta"
                            className="btn-primary mt-10 inline-flex group"
                        >
                            {t("about.text6", "Start a conversation")}
                            <span className="transition-transform duration-300 group-hover:translate-x-1">
                                →
                            </span>
                        </Link>
                    </Reveal>
                </div>
            </section>

            <CtaBand />
        </>
    );
};
